/** Magnitud ordenada: CPM por categoría, sesgo por parámetro, score por desafío.
 *  Es magnitud, no identidad → SECUENCIAL de una hue. El bigote marca el intervalo cuando existe. */
import { useState } from "react";
import type { Grafico, Idioma, Serie as SerieT } from "../lib/tipos";
import { num } from "../lib/formato";
import { ANCHO_MOVIL, Marco, Tooltip, pasoSecuencial, useEsMovil } from "./comun";

const ANCHO = 620;
const ALTO_BARRA = 24;
const IZQ = 190;

export function GraficoBarras({ grafico, idioma }: { grafico: Grafico; idioma: Idioma }) {
  const serie = ((grafico.series ?? []) as SerieT[])[0];
  const [activo, setActivo] = useState<number | null>(null);
  const movil = useEsMovil();
  const puntos = (serie?.puntos ?? []).filter((p) => p.y !== null) as { x: string | number; y: number; y_inf?: number; y_sup?: number; riesgo_id?: string }[];
  if (!puntos.length) return null;

  const conNegativos = puntos.some((p) => p.y < 0);
  const max = Math.max(...puntos.map((p) => Math.abs(p.y)), ...puntos.flatMap((p) => [Math.abs(p.y_sup ?? 0), Math.abs(p.y_inf ?? 0)])) || 1;

  // En móvil la etiqueta va ENCIMA de la barra, no al lado: con `IZQ = 190` dentro de un viewBox
  // de 360 quedarían 100 px de barra y la comparación visual —que es para lo que existe este
  // gráfico— desaparece. El valor va en la misma línea que la etiqueta, alineado a la derecha,
  // donde nunca choca con nada.
  const anchoV = movil ? ANCHO_MOVIL : ANCHO;
  const izq = movil ? 0 : IZQ;
  const altoBarra = movil ? 20 : ALTO_BARRA;
  const altoEtiqueta = movil ? 17 : 0;            // la línea de texto sobre la barra
  const paso = altoBarra + altoEtiqueta + (movil ? 12 : 8);
  const cuerpo = movil ? 13 : 11;                 // a escala ~0,71 son ~9 px reales
  const util = anchoV - izq - (movil ? 4 : 70);
  const cero = conNegativos ? izq + util / 2 : izq;
  const escala = conNegativos ? util / 2 / max : util / max;
  const alto = 12 + puntos.length * paso;
  const maxCaracteres = movil ? 34 : 28;

  return (
    <Marco titulo={grafico.titulo} idioma={idioma} ancho={anchoV} alto={alto}>
      <svg viewBox={`0 0 ${anchoV} ${alto}`} width="100%" height="100%" onMouseLeave={() => setActivo(null)}>
        {conNegativos && <line x1={cero} x2={cero} y1={6} y2={alto - 6} stroke="var(--borde-fuerte)" strokeWidth="1" />}
        {grafico.referencia !== undefined && (
          <line x1={cero + grafico.referencia * escala} x2={cero + grafico.referencia * escala} y1={6} y2={alto - 6}
                stroke="var(--borde-fuerte)" strokeWidth="1" strokeDasharray="4 3" />
        )}
        {puntos.map((p, i) => {
          const fila = 6 + i * paso;
          const y = fila + altoEtiqueta;          // la barra, bajo su etiqueta en móvil
          const w = Math.abs(p.y) * escala;
          const x = p.y < 0 ? cero - w : cero;
          const etiqueta = String(p.x);
          return (
            <g key={`${etiqueta}${i}`} onMouseEnter={() => setActivo(i)}>
              <text x={movil ? 0 : IZQ - 10} y={movil ? fila + 11 : y + altoBarra / 2}
                    textAnchor={movil ? "start" : "end"} dominantBaseline={movil ? undefined : "middle"}
                    fontSize={cuerpo} fill="var(--texto-2)">
                {etiqueta.length > maxCaracteres ? `${etiqueta.slice(0, maxCaracteres - 1)}…` : etiqueta}
              </text>
              {/* Extremo redondeado de 4px solo en el lado del dato; el otro queda anclado a la base. */}
              <path d={barra(x, y, Math.max(w, 2), altoBarra, p.y < 0 ? "izq" : "der")}
                    fill={p.riesgo_id ? colorRiesgo(p.riesgo_id) : pasoSecuencial(Math.abs(p.y), max)}
                    fillOpacity={activo === null || activo === i ? 1 : 0.55} />
              {p.y_inf !== undefined && p.y_sup !== undefined && (
                <g stroke="var(--texto-2)" strokeWidth="1.5">
                  <line x1={cero + p.y_inf * escala} x2={cero + p.y_sup * escala} y1={y + altoBarra / 2} y2={y + altoBarra / 2} />
                  <line x1={cero + p.y_inf * escala} x2={cero + p.y_inf * escala} y1={y + 5} y2={y + altoBarra - 5} />
                  <line x1={cero + p.y_sup * escala} x2={cero + p.y_sup * escala} y1={y + 5} y2={y + altoBarra - 5} />
                </g>
              )}
              {/* El valor: en escritorio al final de la barra; en móvil en la línea de la
                  etiqueta, alineado a la derecha, para no chocar con una barra casi llena. */}
              <text x={movil ? anchoV : (p.y < 0 ? x - 8 : x + w + 8)}
                    y={movil ? fila + 11 : y + altoBarra / 2} dominantBaseline={movil ? undefined : "middle"}
                    textAnchor={movil ? "end" : (p.y < 0 ? "end" : "start")} fontSize={cuerpo} fill="var(--texto-1)">
                {num(p.y, Math.abs(p.y) < 10 ? 2 : 1)}
              </text>
            </g>
          );
        })}
        {activo !== null && puntos[activo] && (
          <Tooltip x={cero + Math.abs(puntos[activo]!.y) * escala} y={6 + activo * paso + altoEtiqueta} ancho={anchoV}
                   lineas={[String(puntos[activo]!.x), `${num(puntos[activo]!.y, 3)}${grafico.unidad ? ` ${grafico.unidad}` : ""}`,
                            ...(puntos[activo]!.y_inf !== undefined ? [`IC: ${num(puntos[activo]!.y_inf!, 2)} … ${num(puntos[activo]!.y_sup!, 2)}`] : [])]} />
        )}
      </svg>
    </Marco>
  );
}

function colorRiesgo(riesgo: string): string {
  return riesgo === "significant" ? "var(--estado-serio)" : riesgo === "minor" ? "var(--estado-aviso)" : "var(--sec-2)";
}

function barra(x: number, y: number, w: number, h: number, lado: "izq" | "der"): string {
  const r = Math.min(4, w);
  if (lado === "der") {
    return `M${x},${y} H${x + w - r} a${r},${r} 0 0 1 ${r},${r} V${y + h - r} a${r},${r} 0 0 1 -${r},${r} H${x} Z`;
  }
  return `M${x + w},${y} H${x + r} a${r},${r} 0 0 0 -${r},${r} V${y + h - r} a${r},${r} 0 0 0 ${r},${r} H${x + w} Z`;
}
